import type { AuditAction, AuditContext, AuditLogEntry as Entry, SignInMethod } from '@mads/shared'
import { ErrorNote, Loading } from '@/components/Feedback'
import { useAuditLog, useMyAuditLog, useUserAuditLog } from '@/lib/queries'

/**
 * A label per action, because an action without one renders as a blank row.
 * Every case in `AuditAction` needs an entry here — the `Record` is what makes
 * adding one to the union a type error until it does (issue #59).
 *
 * The `user_*` sign-in cases (issue #58) are the first of these to be read by
 * two panels at once: an account's own history on `/profile`, and an admin's
 * copy of it on the user screen. They are worded as things that happened to the
 * account, because that is how both readers meet them — "Signed in", not "Sign
 * in".
 */
const ACTION_LABELS: Record<AuditAction, string> = {
  item_created: 'Created',
  item_updated: 'Updated',
  item_deleted: 'Deleted',
  item_restored: 'Restored',
  item_purged: 'Destroyed',
  item_stocktake_adjusted: 'Stocktake',
  location_created: 'Created',
  location_updated: 'Updated',
  location_deleted: 'Binned',
  location_restored: 'Restored',
  location_purged: 'Destroyed',
  locations_reordered: 'Reordered',
  fixture_model_created: 'Created',
  fixture_model_updated: 'Updated',
  fixture_model_deleted: 'Removed',
  user_created: 'Added',
  user_updated: 'Updated',
  user_deactivated: 'Deactivated',
  user_deleted: 'Removed',
  user_signed_in: 'Signed in',
  user_password_accepted: 'Password accepted',
  user_sign_in_failed: 'Wrong password',
  user_sign_in_throttled: 'Too many attempts',
  user_sign_in_refused: 'Turned away',
  user_second_factor_failed: 'Second step failed',
  user_password_changed: 'Password changed',
  user_password_reset: 'Password reset',
  user_passkey_added: 'Passkey added',
  user_passkey_removed: 'Passkey removed',
  user_devices_forgotten: 'Devices forgotten',
  stocktake_started: 'Started',
  stocktake_completed: 'Completed',
  stocktake_abandoned: 'Abandoned',
  changes_synced: 'Offline changes synced',
}

/**
 * The attention states are the brass secondary's, and these genuinely are: a
 * wrong password on your account is the row you are scanning for. The rest stay
 * neutral, so the ones that matter are the ones that stand out.
 */
const ATTENTION_ACTIONS: ReadonlySet<AuditAction> = new Set<AuditAction>([
  'user_sign_in_failed',
  'user_sign_in_throttled',
  'user_sign_in_refused',
  'user_second_factor_failed',
])

/** How a sign-in was made, said the way somebody would say it out loud. */
const METHOD_LABELS: Record<SignInMethod, string> = {
  password: 'with a password',
  passkey: 'with a passkey',
  sms: 'with a texted code',
  'trusted-device': 'on a trusted browser',
  google: 'with Google',
}

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * The method and where from (issue #58) — the second half of "was that me?",
 * which the timestamp alone cannot answer.
 *
 * The user agent is deliberately not shown. It is a caller-controlled string
 * seventy characters long that nobody can read, and the browser name it
 * contains is already on the trusted-devices list where it means something.
 */
function describeContext(context: AuditContext): string | null {
  const parts: string[] = []

  if (context.method !== undefined) parts.push(METHOD_LABELS[context.method])
  if (context.ip !== undefined && context.ip !== '') parts.push(`from ${context.ip}`)

  return parts.length === 0 ? null : parts.join(', ')
}

/**
 * The entries themselves, given to it rather than fetched — three panels render
 * this list from three different queries (an item's history, an account's, and
 * your own), and the differences between them are all in the fetching.
 */
export function AuditEntries({ entries }: { entries: Entry[] }) {
  return (
    <ul className="audit-log">
      {entries.map((entry) => {
        const context = entry.context === null ? null : describeContext(entry.context)

        return (
          <li key={entry.id} className="audit-log-entry">
            <div className="audit-log-meta">
              {/* The sign-in labels are two and three words where the item ones
                  were one, and a tag broken across lines reads as two tags. */}
              <span
                style={{ whiteSpace: 'nowrap' }}
                className={
                  ATTENTION_ACTIONS.has(entry.action) ? 'tag tag-accent-2' : 'tag tag-neutral'
                }
              >
                {ACTION_LABELS[entry.action]}
              </span>
              <span className="text-muted">
                {entry.actorName} · {formatTimestamp(entry.createdAt)}
                {context !== null && ` · ${context}`}
              </span>
            </div>

            {entry.changes !== null && entry.changes.length > 0 && (
              <ul className="audit-log-changes">
                {entry.changes.map((change) => (
                  <li key={change.field}>
                    <strong>{change.label}:</strong> {change.from ?? '—'} &rarr; {change.to ?? '—'}
                  </li>
                ))}
              </ul>
            )}
          </li>
        )
      })}
    </ul>
  )
}

/**
 * An item's change history: who created or edited it and when, with the
 * field-level before/after for each edit.
 */
export function AuditLog({ itemId }: { itemId: number }) {
  const { data: entries, isPending, error } = useAuditLog(itemId)

  /*
   * The heading stays up while this is waiting, which matters most when it is
   * waiting forever: an item's history is deliberately not kept on the device
   * (issue #64's allowlist — it is the one thing on this screen that is about
   * people rather than kit), so offline `Loading` says "this screen has not
   * been saved for use without a connection". Without a heading over it that
   * sentence reads as a verdict on the whole record, which since issue #70 is
   * flatly untrue — everything above it is there.
   */
  return (
    <>
      <div className="section-title">Change history</div>

      {isPending ? (
        <Loading what="Loading the change history" />
      ) : error !== null ? (
        <ErrorNote error={error} />
      ) : entries.length === 0 ? (
        <p className="empty-note">No changes recorded yet.</p>
      ) : (
        <AuditEntries entries={entries} />
      )}
    </>
  )
}

/**
 * One account's history, for the admin looking after it (issue #58) — sign-ins
 * and refusals alongside the edits made to the record.
 */
export function UserAuditLog({ userId, enabled }: { userId: number; enabled: boolean }) {
  const { data: entries, isPending, error } = useUserAuditLog(userId, enabled)

  if (!enabled) return null
  if (isPending) return <Loading what="Loading the account history" />
  if (error !== null) return <ErrorNote error={error} />

  return (
    <>
      <div className="section-title">Account history</div>
      <p className="text-muted" style={{ fontSize: 13 }}>
        Sign-ins, refused attempts, and every change made to this record.
      </p>

      {entries.length === 0 ? (
        <p className="empty-note">Nothing recorded yet.</p>
      ) : (
        <AuditEntries entries={entries} />
      )}
    </>
  )
}

/**
 * Your own history, on `/profile` (issue #58). The account holder gets this
 * before an admin does, deliberately: "your account was used from a browser you
 * don't recognise on Tuesday" is useful to the person it is about first.
 */
export function MyAuditLog() {
  const { data: entries, isPending, error } = useMyAuditLog()

  return (
    <section style={{ maxWidth: 520, marginTop: 'var(--space-8)' }}>
      <h2 className="section-title" style={{ marginTop: 0 }}>
        Recent activity
      </h2>
      <p className="text-muted" style={{ fontSize: 13 }}>
        Every sign-in to your account, and every attempt that didn&rsquo;t work. If something here
        wasn&rsquo;t you, change your password and tell the Head of Tech.
      </p>

      {isPending ? (
        <Loading what="Loading your recent activity" />
      ) : error !== null ? (
        <ErrorNote error={error} />
      ) : entries.length === 0 ? (
        <p className="empty-note">Nothing recorded yet.</p>
      ) : (
        <AuditEntries entries={entries} />
      )}
    </section>
  )
}
